import { useAuthContext } from '@caterinx/api-client';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { PasswordInput } from '@caterinx/ui/components/ui/password-input';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { isAxiosError } from 'axios';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { authQuery, safeRedirect } from '../../lib/redirect.js';

interface LoginResponse {
  token: string;
  refreshToken: string;
}

export function LoginPage() {
  const { setToken, setRefreshToken } = useAuthContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Vorbelegt, wenn die Einladungsseite die Adresse mitgibt (D32); bleibt editierbar.
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirect = safeRedirect(searchParams.get('redirect'));

  const handleSubmit = async (formEvent: FormEvent): Promise<void> => {
    formEvent.preventDefault();
    setIsSubmitting(true);

    try {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      setToken(data.token);
      setRefreshToken(data.refreshToken);

      // Ziel aus dem redirect-Parameter, den ProtectedRoute gesetzt hat — inklusive
      // Query-String. Sonst landet der Nutzer nach dem Login auf einer leeren Seite.
      // Nur In-App-Pfade: ein `redirect` auf eine fremde Domain wäre ein Open Redirect.
      navigate(redirect ?? '/', { replace: true });
    } catch (error) {
      // Der strukturierte `code` aus der API steuert die Reaktion, nicht der Text.
      const code = isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined;

      if (code === 'USER_NOT_CONFIRMED') {
        toast({
          title: 'E-Mail noch nicht bestätigt',
          description: 'Bitte klicken Sie den Link in der Bestätigungsmail.',
        });
        navigate(`/verify-email?${authQuery({ email, redirect })}`);
        return;
      }

      toast({
        variant: 'destructive',
        title: 'Anmeldung fehlgeschlagen',
        description:
          code === 'INVALID_CREDENTIALS'
            ? 'E-Mail oder Passwort ist falsch.'
            : 'Bitte versuchen Sie es in einem Moment erneut.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-4">
      <Wordmark className="w-44" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Anmelden</CardTitle>
          <CardDescription>Zugang zum caterinx-Lieferantenportal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(changeEvent) => setEmail(changeEvent.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(changeEvent) => setPassword(changeEvent.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Wird geprüft...' : 'Anmelden'}
            </Button>
          </form>

          <div className="text-muted-foreground mt-6 space-y-2 text-sm">
            <p>
              <Link to="/forgot-password" className="underline">
                Passwort vergessen?
              </Link>
            </p>
            <p>
              Noch kein Zugang?{' '}
              <Link to={redirect ? `/register?${authQuery({ email, redirect })}` : '/register'} className="underline">
                Registrieren
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
